/**
 * Iter 109 — Generic rate-row detail editor for Iron builder
 * Sheet / Ridge / Barge / Valley / Flashing sections.
 *
 * Adopts the same visual format as `RescrewDetailModal` (Iter 106b):
 * full-page overlay, emerald/teal gradient header, single-table body
 * with editable inline cells, supply-panel expand toggle, emerald-
 * highlighted "Rate (sales)" cell, per-m totals footer, save-target
 * radio. Field set is configurable per component so the operator
 * sees columns relevant to the rate-row type:
 *
 *   • Sheet     → Material · Profile · Composition · Gauge · Cover ·
 *                 Buy $/m · Materials $/m · Labour $/m · Contractor $/m
 *   • Ridge     → Material · Class · Width · Length · Work Type ·
 *                 Buy + Materials + Labour + Contractor /m
 *   • Barge     → same as Ridge
 *   • Valley    → Material · Work · Profile · Class · /m rates
 *   • Flashing  → Material · Width · Folds · /m rates
 *
 * Save target radio (Override-only / Apply to master) is hidden when
 * `overrideKey` is null because the legacy per-pin sections support
 * `rate_override` (one number) but not the structured override block
 * the rescrew section uses. Setting `overrideKey` to a path on the
 * appraisal config would unlock that flow in a follow-up.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, X, Save, Loader2, AlertCircle } from 'lucide-react';

const COMPONENT_PRESETS = {
  sheet: {
    title: 'Long-Run Sheet',
    rateBasis: '/m',
    unit: 'm',
    fields: [
      { key: 'material',         label: 'Material',        kind: 'text', placeholder: 'Colorsteel' },
      { key: 'profile',          label: 'Profile',         kind: 'text', placeholder: 'Trimrib' },
      { key: 'length_mm',        label: 'Length (mm)',     kind: 'number', step: 1, unit: 'mm' },
      { key: 'buy_cost',         label: 'Buy $/m',         kind: 'number', step: 0.01, unit: '$' },
      { key: 'materials_rate',   label: 'Material $/m',    kind: 'number', step: 0.01, unit: '$' },
      { key: 'labour_hours_per_m2', label: 'Hours / m',    kind: 'number', step: 0.01, unit: 'hrs' },
      { key: 'labour_rate',      label: 'Labour $/m',      kind: 'number', step: 0.01, unit: '$' },
      { key: 'contractor_rate',  label: 'Contractor $/m',  kind: 'number', step: 0.01, unit: '$' },
    ],
  },
  ridge: {
    title: 'Iron Ridge cap / rescrew',
    rateBasis: '/m',
    unit: 'm',
    fields: [
      { key: 'material',         label: 'Material',        kind: 'text', placeholder: 'Galv / Colorsteel' },
      { key: 'class',            label: 'Class',           kind: 'text', placeholder: 'Folded / Pressed' },
      { key: 'work_type',        label: 'Work Type',       kind: 'text', placeholder: 'Replace / Rescrew' },
      { key: 'length_mm',        label: 'Length (mm)',     kind: 'number', step: 1, unit: 'mm' },
      { key: 'buy_cost',         label: 'Buy $/m',         kind: 'number', step: 0.01, unit: '$' },
      { key: 'materials_rate',   label: 'Material $/m',    kind: 'number', step: 0.01, unit: '$' },
      { key: 'labour_hours_per_m2', label: 'Hours / m',    kind: 'number', step: 0.01, unit: 'hrs' },
      { key: 'labour_rate',      label: 'Labour $/m',      kind: 'number', step: 0.01, unit: '$' },
      { key: 'contractor_rate',  label: 'Contractor $/m',  kind: 'number', step: 0.01, unit: '$' },
    ],
  },
  barge: {
    title: 'Iron Barge cap / rescrew',
    rateBasis: '/m',
    unit: 'm',
    fields: [
      { key: 'material',         label: 'Material',        kind: 'text', placeholder: 'Galv / Colorsteel' },
      { key: 'class',            label: 'Class',           kind: 'text', placeholder: 'Folded / Pressed' },
      { key: 'work_type',        label: 'Work Type',       kind: 'text', placeholder: 'Replace / Rescrew' },
      { key: 'length_mm',        label: 'Length (mm)',     kind: 'number', step: 1, unit: 'mm' },
      { key: 'buy_cost',         label: 'Buy $/m',         kind: 'number', step: 0.01, unit: '$' },
      { key: 'materials_rate',   label: 'Material $/m',    kind: 'number', step: 0.01, unit: '$' },
      { key: 'labour_hours_per_m2', label: 'Hours / m',    kind: 'number', step: 0.01, unit: 'hrs' },
      { key: 'labour_rate',      label: 'Labour $/m',      kind: 'number', step: 0.01, unit: '$' },
      { key: 'contractor_rate',  label: 'Contractor $/m',  kind: 'number', step: 0.01, unit: '$' },
    ],
  },
  valley: {
    title: 'Iron Valley',
    rateBasis: '/m',
    unit: 'm',
    fields: [
      { key: 'material',         label: 'Material',        kind: 'text', placeholder: 'Galv / Zinc / Colorsteel' },
      { key: 'profile',          label: 'Profile',         kind: 'text', placeholder: 'Folded valley' },
      { key: 'work_type',        label: 'Work Type',       kind: 'text', placeholder: 'Replace' },
      { key: 'width_mm',         label: 'Width (mm)',      kind: 'number', step: 1, unit: 'mm' },
      { key: 'buy_cost',         label: 'Buy $/m',         kind: 'number', step: 0.01, unit: '$' },
      { key: 'materials_rate',   label: 'Material $/m',    kind: 'number', step: 0.01, unit: '$' },
      { key: 'labour_hours_per_m2', label: 'Hours / m',    kind: 'number', step: 0.01, unit: 'hrs' },
      { key: 'labour_rate',      label: 'Labour $/m',      kind: 'number', step: 0.01, unit: '$' },
      { key: 'contractor_rate',  label: 'Contractor $/m',  kind: 'number', step: 0.01, unit: '$' },
    ],
  },
  flashing: {
    title: 'Iron Flashing / Btray / Dektite',
    rateBasis: '/m',
    unit: 'm',
    fields: [
      { key: 'material',         label: 'Material',        kind: 'text', placeholder: 'Aluminium / EPDM' },
      { key: 'width_mm',         label: 'Width (mm)',      kind: 'number', step: 1, unit: 'mm' },
      { key: 'buy_cost',         label: 'Buy $/m',         kind: 'number', step: 0.01, unit: '$' },
      { key: 'materials_rate',   label: 'Material $/m',    kind: 'number', step: 0.01, unit: '$' },
      { key: 'labour_hours_per_m2', label: 'Hours / m',    kind: 'number', step: 0.01, unit: 'hrs' },
      { key: 'labour_rate',      label: 'Labour $/m',      kind: 'number', step: 0.01, unit: '$' },
      { key: 'contractor_rate',  label: 'Contractor $/m',  kind: 'number', step: 0.01, unit: '$' },
    ],
  },
  // Iter 121 — Tin Tile presets.
  fastening: {
    title: 'Fastening — Rescrew / TimberTek',
    rateBasis: '/m',
    unit: 'm',
    fields: [
      { key: 'work_type',        label: 'Work Type',       kind: 'text', placeholder: 'Rescrew / Bottom course / Ridging' },
      { key: 'screw_size',       label: 'Screw Size',      kind: 'text', placeholder: '12*50 / 12*65' },
      { key: 'ratio',            label: 'Qty / m²',        kind: 'number', step: 0.1, unit: 'ea' },
      { key: 'buy_cost',         label: 'Buy $/m',         kind: 'number', step: 0.01, unit: '$' },
      { key: 'materials_rate',   label: 'Material $/m',    kind: 'number', step: 0.01, unit: '$' },
      { key: 'labour_hours_per_m2', label: 'Hours / m',    kind: 'number', step: 0.01, unit: 'hrs' },
      { key: 'labour_rate',      label: 'Labour $/m',      kind: 'number', step: 0.01, unit: '$' },
      { key: 'contractor_rate',  label: 'Contractor $/m',  kind: 'number', step: 0.01, unit: '$' },
    ],
  },
  tile: {
    title: 'Tin tile (replace)',
    rateBasis: '/ea',
    unit: 'ea',
    fields: [
      { key: 'material',         label: 'Material',        kind: 'text', placeholder: 'Decramastic / Colortile' },
      { key: 'profile',          label: 'Profile',         kind: 'text', placeholder: 'Bond / Shake / Roman' },
      { key: 'buy_cost',         label: 'Buy $/ea',        kind: 'number', step: 0.01, unit: '$' },
      { key: 'materials_rate',   label: 'Material $/ea',   kind: 'number', step: 0.01, unit: '$' },
      { key: 'labour_hours_per_m2', label: 'Hours / ea',   kind: 'number', step: 0.01, unit: 'hrs' },
      { key: 'labour_rate',      label: 'Labour $/ea',     kind: 'number', step: 0.01, unit: '$' },
      { key: 'contractor_rate',  label: 'Contractor $/ea', kind: 'number', step: 0.01, unit: '$' },
    ],
  },
};

// Supply-row fields (common to all components — surface in the
// expanded supply panel, matching the rescrew modal layout).
const SUPPLY_FIELDS = [
  { key: 'materials_desc', label: 'Materials description', col: 5, placeholder: 'e.g. 0.55mm Colorsteel Trimrib · 5400mm' },
  { key: 'supplier_code',  label: 'Product code',          col: 3, placeholder: 'TRIMRIB-COL-55-5400', mono: true },
  { key: 'supplier',       label: 'Supplier',              col: 4, placeholder: 'Allfast' },
];


export default function IronRateDetailModal({ api, open, onOpenChange, component, rate, onSave, pinContext }) {
  const preset = COMPONENT_PRESETS[component] || COMPONENT_PRESETS.sheet;
  const [draft, setDraft] = useState({});
  const [supplyOpen, setSupplyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Iter 110 — save-target switch is back. When `pinContext` is set
  // (operator launched the modal from a per-pin / per-sheet row),
  // the default save path is the structured per-pin override that
  // lives on the appraisal's iron-repairs config — master rate row
  // is untouched. The operator can flip the radio to "Apply to
  // master rate" to cascade the change instead.
  const hasPinContext = !!pinContext;
  const [target, setTarget] = useState(hasPinContext ? 'override' : 'master');

  useEffect(() => {
    if (!open || !rate) return;
    // Hydrate from the rate row — pull every field listed in the
    // preset's field list, plus the supply panel fields. Per-pin
    // override values (when present) take precedence so the operator
    // sees the *effective* numbers — easier to edit than two columns
    // of "master vs override".
    const ov = pinContext?.detailOverride || {};
    const next = { name: ov.name || rate.name || '' };
    for (const f of preset.fields) {
      const fromOpt =
        f.key === 'buy_cost'        ? (rate.buy_per_lm ?? rate.buy_per_m ?? rate.buy ?? rate.buy_each) :
        f.key === 'materials_rate'  ? (rate.material_per_lm ?? rate.material_per_m ?? rate.material ?? rate.material_each) :
        f.key === 'labour_rate'     ? (rate.labour_per_lm ?? rate.labour_per_m ?? rate.labour ?? rate.labour_each) :
        f.key === 'contractor_rate' ? (rate.contractor_per_lm ?? rate.contractor_per_m ?? rate.contractor ?? rate.contractor_each) :
        f.key === 'length_mm'       ? (rate.length_mm ?? rate.len_mm) :
        rate[f.key];
      next[f.key] = ov[f.key] ?? fromOpt ?? (f.kind === 'number' ? 0 : '');
    }
    for (const sf of SUPPLY_FIELDS) {
      next[sf.key] = ov[sf.key] ?? rate[sf.key] ?? '';
    }
    setDraft(next);
    setErr(null);
    setSupplyOpen(!!(rate.materials_desc || rate.supplier_code || rate.supplier || Object.keys(ov).length > 0));
    setTarget(hasPinContext ? 'override' : 'master');
  }, [open, rate, preset, pinContext, hasPinContext]);

  // Derived rate per <basis> (live preview).
  const chargeRate = useMemo(() => {
    return Math.round(((Number(draft.materials_rate) || 0) + (Number(draft.labour_rate) || 0)) * 100) / 100;
  }, [draft.materials_rate, draft.labour_rate]);
  const hoursPerM = Number(draft.labour_hours_per_m2) || 0;
  const labourPerHr     = hoursPerM > 0 ? (Number(draft.labour_rate) || 0) / hoursPerM : 0;
  const contractorPerHr = hoursPerM > 0 ? (Number(draft.contractor_rate) || 0) / hoursPerM : 0;

  const patchField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const handleSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body = {};
      for (const k of Object.keys(draft)) {
        const v = draft[k];
        if (v === undefined || v === null) continue;
        body[k] = v;
      }
      if (target === 'override' && hasPinContext && pinContext?.onSaveOverride) {
        // Iter 110 — per-pin override path. Caller persists onto the
        // appraisal's iron-repairs config item without touching the
        // master rate row.
        await pinContext.onSaveOverride(body);
      } else {
        // Master-rate path — PATCH the catalogue row directly.
        await api.patch(`/restoration/iron-repairs/rate-detail/${rate.id}`, body);
        if (onSave) await onSave();
      }
      onOpenChange(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open || !rate) return null;

  // Decide column counts so the table footer aligns under the rate
  // cell on the right (same layout as the rescrew modal).
  const numericCols = preset.fields.filter(f => f.kind === 'number' && !['buy_cost', 'materials_rate', 'labour_rate', 'contractor_rate'].includes(f.key));
  const textCols    = preset.fields.filter(f => f.kind === 'text');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={() => onOpenChange(false)}
         data-testid="iron-rate-detail-modal"
         data-component={component}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Edit master rate · {component}</div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mt-0.5">
              <Calculator size={18} className="text-emerald-700" /> {rate.name}
            </h3>
            <div className="text-[11px] text-slate-500 mt-0.5">Iron Restoration · {preset.title}</div>
            <div className="text-[11px] text-slate-600 mt-1">
              Sales rate per {preset.rateBasis.replace('/', '')} = <strong className="text-emerald-800">Material + Labour</strong>.
              Buy $/{preset.rateBasis.replace('/', '')} is the wholesale supplier cost. Contractor is sub-contracted
              labour cost — both internal, neither changes the quote.
            </div>
          </div>
          <button type="button" onClick={() => onOpenChange(false)} className="text-slate-400 hover:text-slate-700" data-testid="iron-rate-detail-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {hasPinContext && pinContext?.hideMaster ? (
            // Iter148 — `pinContext.hideMaster` forces override-only mode.
            // Used by sites whose underlying rate row lives in a
            // different collection (e.g. AppraisalComponentsTab → the
            // `component_rates` overlay rather than the iron-repairs
            // collection the master-apply endpoint targets). Hiding the
            // radio keeps the operator from triggering a master-apply
            // that would 404.
            <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-4 text-xs text-blue-900">
              <strong>Override for {pinContext.label || 'this row'}</strong> — values land on this appraisal only.
              Master catalogue rate is untouched. Other appraisals are unaffected. Edit the catalogue directly via
              <em> /component-rates</em> if you want the change to propagate.
            </div>
          ) : hasPinContext ? (
            <div className="bg-slate-50 border border-slate-200 rounded p-2 mb-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                Save target — {pinContext.label || 'pin'}
              </div>
              <label className="flex items-start gap-2 text-xs cursor-pointer py-1">
                <input type="radio" name="iron-rate-detail-target" value="override"
                       checked={target === 'override'}
                       onChange={() => setTarget('override')}
                       data-testid="iron-rate-detail-target-override"
                       className="mt-0.5 text-[#D35400]" />
                <div>
                  <div className="font-semibold text-slate-800">Override for this pin only</div>
                  <div className="text-slate-500">Values land on the current quote's {pinContext.label || 'pin'} — master rate row untouched. Other pins / appraisals are unaffected.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 text-xs cursor-pointer py-1">
                <input type="radio" name="iron-rate-detail-target" value="master"
                       checked={target === 'master'}
                       onChange={() => setTarget('master')}
                       data-testid="iron-rate-detail-target-master"
                       className="mt-0.5 text-emerald-700" />
                <div>
                  <div className="font-semibold text-slate-800">Apply to master rate</div>
                  <div className="text-slate-500">Update the catalogue row — every appraisal that references this rate will pick up the change on next reload.</div>
                </div>
              </label>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-4 text-xs text-amber-900">
              <strong>Heads-up</strong> — this updates the catalogue rate row directly. Every appraisal that references this rate
              will pick up your change on next reload. For per-job overrides without touching the master, use the existing
              <em> rate override</em> field on the pin row in the builder.
            </div>
          )}

          {/* Editable table — same shape as the rescrew modal. */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="iron-rate-detail-table">
              <thead className="text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pl-2 w-6"></th>
                  {textCols.map(f => (
                    <th key={f.key} className="text-left py-2 pr-2">{f.label}</th>
                  ))}
                  {numericCols.map(f => (
                    <th key={f.key} className="text-right py-2 px-1.5">{f.label}</th>
                  ))}
                  <th className="text-right py-2 px-1.5">Buy $/{preset.unit || 'm'}</th>
                  <th className="text-right py-2 px-1.5">Material $/{preset.unit || 'm'}</th>
                  <th className="text-right py-2 px-1.5">Labour $/{preset.unit || 'm'}</th>
                  <th className="text-right py-2 px-1.5">Contractor $/{preset.unit || 'm'}</th>
                  <th className="text-right py-2 pl-2 bg-emerald-50">Rate (sales)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100" data-testid="iron-rate-detail-row">
                  <td className="py-1.5 pl-2">
                    <button
                      type="button"
                      data-testid="iron-rate-detail-supply-toggle"
                      onClick={() => setSupplyOpen(o => !o)}
                      title={supplyOpen ? 'Hide supply inputs' : 'Show supply inputs (description / code / supplier / hours)'}
                      className={`w-5 h-5 inline-flex items-center justify-center rounded text-xs font-bold transition-all ${
                        (draft.materials_desc || draft.supplier_code || draft.supplier)
                          ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      }`}
                    >{supplyOpen ? '−' : '+'}</button>
                  </td>
                  {textCols.map(f => (
                    <td key={f.key} className="py-1.5 pr-2">
                      <input type="text"
                             value={draft[f.key] || ''}
                             data-testid={`iron-rate-detail-${f.key}`}
                             onChange={(e) => patchField(f.key, e.target.value)}
                             placeholder={f.placeholder || ''}
                             className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400" />
                    </td>
                  ))}
                  {numericCols.map(f => (
                    <td key={f.key} className="py-1.5 px-1.5">
                      <NumericCell value={draft[f.key]} unit={f.unit} step={f.step} testid={`iron-rate-detail-${f.key}`}
                                   onChange={(v) => patchField(f.key, v)} />
                    </td>
                  ))}
                  {['buy_cost', 'materials_rate', 'labour_rate', 'contractor_rate'].map(k => (
                    <td key={k} className="py-1.5 px-1.5">
                      <NumericCell value={draft[k]} unit="$" step={0.01} testid={`iron-rate-detail-${k}`}
                                   onChange={(v) => patchField(k, v)} />
                    </td>
                  ))}
                  <td className="py-1.5 pl-2 text-right font-mono-num font-bold text-emerald-800 bg-emerald-50" data-testid="iron-rate-detail-rate">
                    ${chargeRate.toFixed(2)}
                  </td>
                </tr>

                {supplyOpen && (
                  <tr className="border-b border-slate-100 bg-teal-50/40" data-testid="iron-rate-detail-supply-row">
                    <td></td>
                    <td colSpan={textCols.length + numericCols.length + 5} className="py-2 pr-2 pl-2">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700 mb-1.5">
                        Material supply &amp; labour timing
                      </div>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        {SUPPLY_FIELDS.map(sf => (
                          <label key={sf.key} className={`col-span-${sf.col} flex flex-col gap-0.5`}>
                            <span className="text-[10px] text-slate-500 font-semibold">{sf.label}</span>
                            <input
                              type="text"
                              value={draft[sf.key] || ''}
                              data-testid={`iron-rate-detail-${sf.key}`}
                              onChange={(e) => patchField(sf.key, e.target.value)}
                              placeholder={sf.placeholder}
                              className={`px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-400 ${sf.mono ? 'font-mono' : ''}`}
                            />
                          </label>
                        ))}
                        <label className="col-span-3 flex flex-col gap-0.5">
                          <span className="text-[10px] text-slate-500 font-semibold">Hours / {preset.unit || 'm'}</span>
                          <div className="relative">
                            <input
                              type="number" step="0.01" min="0"
                              value={draft.labour_hours_per_m2 ?? 0}
                              data-testid="iron-rate-detail-labour_hours_per_m2"
                              onChange={(e) => patchField('labour_hours_per_m2', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                              className="w-full pl-1.5 pr-9 py-1 text-xs border border-slate-300 rounded text-right font-mono-num focus:outline-none focus:ring-1 focus:ring-teal-400"
                            />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">hrs</span>
                          </div>
                        </label>
                        <div className="col-span-3 text-center bg-teal-100/70 rounded px-2 py-1">
                          <div className="text-[10px] text-slate-600 font-semibold">Labour $/hr</div>
                          <div className="text-sm font-bold text-teal-900 font-mono-num" data-testid="iron-rate-detail-labour-per-hr">{labourPerHr > 0 ? `$${labourPerHr.toFixed(2)}/hr` : '—'}</div>
                          <div className="text-[9px] text-slate-500">${(Number(draft.labour_rate) || 0).toFixed(2)}/{preset.unit || 'm'} ÷ {hoursPerM > 0 ? hoursPerM.toFixed(2) : '—'} hr</div>
                        </div>
                        <div className="col-span-3 text-center bg-teal-100/70 rounded px-2 py-1">
                          <div className="text-[10px] text-slate-600 font-semibold">Contractor $/hr</div>
                          <div className="text-sm font-bold text-teal-900 font-mono-num" data-testid="iron-rate-detail-contractor-per-hr">{contractorPerHr > 0 ? `$${contractorPerHr.toFixed(2)}/hr` : '—'}</div>
                          <div className="text-[9px] text-slate-500">${(Number(draft.contractor_rate) || 0).toFixed(2)}/{preset.unit || 'm'} ÷ {hoursPerM > 0 ? hoursPerM.toFixed(2) : '—'} hr</div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-bold bg-slate-50">
                  <td colSpan={1 + textCols.length + numericCols.length} className="py-2 pl-2 text-right text-slate-700">Per-{preset.unit || 'm'} totals</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${(Number(draft.buy_cost) || 0).toFixed(2)}</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${(Number(draft.materials_rate) || 0).toFixed(2)}</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${(Number(draft.labour_rate) || 0).toFixed(2)}</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${(Number(draft.contractor_rate) || 0).toFixed(2)}</td>
                  <td className="text-right py-2 pl-2 font-mono-num text-emerald-900 bg-emerald-100" data-testid="iron-rate-detail-total-rate">${chargeRate.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {err && (
            <div data-testid="iron-rate-detail-error" className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-1.5">
              <AlertCircle size={12} /> {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="iron-rate-detail-cancel"
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded">Cancel</button>
          <button type="button" onClick={handleSave} disabled={busy}
                  data-testid="iron-rate-detail-save"
                  className={`px-4 py-1.5 text-xs font-bold text-white rounded disabled:opacity-50 flex items-center gap-1.5 shadow-sm ${
                    target === 'master' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#D35400] hover:bg-[#A04000]'
                  }`}>
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            {busy ? 'Saving…' : (target === 'master' ? 'Apply to master rate' : 'Save override')}
          </button>
        </div>
      </div>
    </div>
  );
}


function NumericCell({ value, unit, step, testid, onChange }) {
  return (
    <div className="relative">
      {unit === '$' && <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">$</span>}
      <input
        type="number"
        step={step ?? 0.01}
        min="0"
        value={value ?? 0}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
        className={`w-full ${unit === '$' ? 'pl-4 pr-1.5' : 'pl-1.5 pr-7'} py-1 text-xs border border-slate-300 rounded text-right font-mono-num focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400`}
      />
      {unit && unit !== '$' && (
        <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-slate-400 pointer-events-none">{unit}</span>
      )}
    </div>
  );
}
